/**
 * The only place that talks to the network.
 *
 * The client computes nothing — it renders what the API returns. If a screen
 * needs a number that isn't in a response, the fix belongs in compute_state,
 * not here. Formatting is presentation and lives in format.js; arithmetic on
 * money does not happen in this codebase at all.
 */

const BASE = import.meta.env?.VITE_API_URL ?? ''
const TOKEN_KEY = 'finance.token'

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token.trim())
  } catch {
    /* private mode — the session still works, it just won't persist */
  }
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message)
    this.status = status
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const token = getToken()
  const response = await fetch(`${BASE}${path}`, {
    method,
    signal,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  let payload = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }

  if (!response.ok) {
    throw new ApiError(response.status, payload?.error || `HTTP ${response.status}`)
  }
  return payload
}

export const api = {
  state: (month) => request(`/state${month ? `?month=${encodeURIComponent(month)}` : ''}`),
  category: (id, month) =>
    request(
      `/categories/${encodeURIComponent(id)}/transactions` +
        (month ? `?month=${encodeURIComponent(month)}` : ''),
    ),
  rule: (id) => request(`/rules/${encodeURIComponent(id)}`),
  transactions: (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
    )
    return request(`/transactions?${query}`)
  },
  flags: (status = 'open') => request(`/flags?status=${status}`),
  flag: (transactionId, note, suggestedCategory) =>
    request('/flags', {
      method: 'POST',
      body: { transaction_id: transactionId, note, suggested_category: suggestedCategory },
    }),
  categories: () => request('/categories'),
  setCap: (categoryId, cap) =>
    request(`/categories/${encodeURIComponent(categoryId)}/cap`, {
      method: 'PATCH',
      body: { cap },
    }),
  createCategory: (id, label, kind, owner, cap) =>
    request('/categories', { method: 'POST', body: { id, label, kind, owner, cap } }),
  recompute: (opts = {}) => request('/recompute', { method: 'POST', body: opts }),
  createSplit: (transactionId, reimbursedAmount, note) =>
    request('/splits', {
      method: 'POST',
      body: { transaction_id: transactionId, reimbursed_amount: reimbursedAmount, note },
    }),
  deleteSplit: (splitId) =>
    request(`/splits/${encodeURIComponent(splitId)}`, { method: 'DELETE' }),
  markOnetime: (transactionId, event, note) =>
    request('/onetime', { method: 'POST', body: { transaction_id: transactionId, event, note } }),
  unmarkOnetime: (onetimeId) =>
    request(`/onetime/${encodeURIComponent(onetimeId)}`, { method: 'DELETE' }),
  events: () => request('/events'),
  createEvent: (name, budget) => request('/events', { method: 'POST', body: { event: name, budget } }),
  eventTransactions: (name) => request(`/events/${encodeURIComponent(name)}/transactions`),
  setEventBudget: (name, budget) =>
    request(`/events/${encodeURIComponent(name)}/budget`, { method: 'PUT', body: { budget } }),
  deleteEventBudget: (name) =>
    request(`/events/${encodeURIComponent(name)}/budget`, { method: 'DELETE' }),
  largePurchases: () => request('/large-purchases'),
  dismissLargePurchase: (transactionId) =>
    request(`/large-purchases/${encodeURIComponent(transactionId)}/dismiss`, { method: 'POST' }),
  auditLog: () => request('/audit-log'),
  requestAudit: (note) => request('/audit/request', { method: 'POST', body: { note } }),
}
